import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { backendService } from "../../../../services/backend.service";
import { type CreateLocationCredentialRequest, CredentialStatus, type LocationCredentialResponse } from "../../../../utils/models";
import type { CredentialSortKey } from "./useSynergyCredentialsState";

const getQueryKey = (departmentId: string, nurseId: number) => ["nurseCredentials", nurseId, departmentId];
const getLocationQueryKey = (departmentId: string, locationId: string) => ["locationCredentials", locationId, departmentId];

export const useCredentialsQueryHook = ({
	nurseId,
	sortKey,
	sortDirection,
}: {
	nurseId: number;
	sortKey: CredentialSortKey;
	sortDirection: "asc" | "desc";
}) => {
	const { track } = useAnalytics();
	const {
		isMedicalDepartment,
		chosenDepartment: { department_id: departmentId },
	} = useHospitalData();
	const {
		data: credentials = [],
		isLoading,
		refetch,
	} = useQuery<LocationCredentialResponse[]>({
		queryKey: getQueryKey(departmentId, nurseId),
		queryFn: async () => {
			track("fetch_nurse_credentials", { departmentId, nurseId });
			const response = await backendService.getNurseCredentials(nurseId, departmentId);
			return response.data;
		},
		initialData: [],
		enabled: !!nurseId && !!departmentId && isMedicalDepartment,
	});

	const sortedCredentials = useMemo(() => {
		return credentials.slice().sort((a, b) => {
			let compareValue = 0;

			switch (sortKey) {
				case "id":
					compareValue = a.id.localeCompare(b.id);
					break;
				case "department_location_id":
					compareValue = a.department_location_id.localeCompare(b.department_location_id);
					break;
				case "created_at":
					compareValue = a.created_at.toMillis() - b.created_at.toMillis();
					break;
				case "expires_at": {
					const dateA = a.expires_at ? a.expires_at.toMillis() : Infinity;
					const dateB = b.expires_at ? b.expires_at.toMillis() : Infinity;
					compareValue = dateA - dateB;
					break;
				}
				case "status":
					compareValue = a.status.localeCompare(b.status);
					break;
			}

			return sortDirection === "asc" ? compareValue : -compareValue;
		});
	}, [credentials, sortKey, sortDirection]);

	return {
		credentials: sortedCredentials,
		isLoading,
		refetch,
	};
};

export const useLocationCredentialsQueryHook = ({
	locationId,
	hideExpired = true,
	onlyShowApproved = false,
}: {
	locationId: string | null | undefined;
	hideExpired?: boolean;
	onlyShowApproved?: boolean;
}) => {
	const { track } = useAnalytics();
	const {
		chosenDepartment: { department_id: departmentId },
	} = useHospitalData();
	const {
		data: credentials = [],
		isLoading,
		refetch,
	} = useQuery<LocationCredentialResponse[]>({
		queryKey: getLocationQueryKey(departmentId, locationId || ""),
		queryFn: async () => {
			if (!locationId) return [];
			const result = await backendService.getLocationCredentials(locationId, departmentId, hideExpired);
			return result.data;
		},
		select: (data) => (onlyShowApproved ? data.filter((cred) => cred.status === CredentialStatus.Approved) : data),
		enabled: !!locationId && !!departmentId,
		staleTime: 10 * 60 * 1000, // 10 minutes,
	});

	const credentialedNurseIds = useMemo(() => {
		return credentials.map((credential) => credential.nurse_id);
	}, [credentials]);

	return {
		credentials,
		credentialedNurseIds,
		isLoading,
		refetch,
	};
};

export const useLocationsCredentialsQueriesHook = ({
	locationIds,
	hideExpired = true,
	onlyShowApproved = false,
}: {
	locationIds: Array<string | null | undefined>;
	hideExpired?: boolean;
	onlyShowApproved?: boolean;
}) => {
	const {
		chosenDepartment: { department_id: departmentId },
	} = useHospitalData();
	const uniqueIds = useMemo(() => Array.from(new Set(locationIds.filter(Boolean))) as string[], [locationIds]);

	const results = useQueries({
		queries: uniqueIds.map((locationId) => ({
			queryKey: getLocationQueryKey(departmentId, locationId),
			queryFn: async () => {
				const response = await backendService.getLocationCredentials(locationId, departmentId, hideExpired);
				return onlyShowApproved ? response.data.filter((cred) => cred.status === CredentialStatus.Approved) : response.data;
			},
			enabled: !!departmentId && !!locationId,
			staleTime: 10 * 60 * 1000,
		})),
	});

	const nurseIdsDeps = results.map((r) => r.dataUpdatedAt).join("|");

	const isLoading = results.some((r) => r.isLoading);
	const isFetching = results.some((r) => r.isFetching);
	const errors = results.map((r) => r.error).filter(Boolean);

	const locationIdsAndCredentials = useMemo(() => {
		const locationIdsAndCredentials = new Map<string, number[]>();
		for (const r of results) {
			for (const result of r.data || []) {
				if (!locationIdsAndCredentials.has(result.department_location_id)) {
					locationIdsAndCredentials.set(result.department_location_id, []);
				}
				locationIdsAndCredentials.get(result.department_location_id)?.push(result.nurse_id);
			}
		}
		return locationIdsAndCredentials;
	}, [
		nurseIdsDeps /*Please do not include `results` variable here,
		this causes a lot of re-computing the same value for credentialedNurseIds,
		we use nurseIdsDeps instead to know when to re-compute*/,
	]);

	const refetchAll = () => Promise.all(results.map((r) => r.refetch()));

	return {
		uniqueLocationIds: uniqueIds,
		locationIdsAndCredentials,
		isLoading,
		isFetching,
		errors,
		refetchAll,
	};
};

export const useCredentialsMutationHooks = () => {
	const {
		chosenDepartment: { department_id: departmentId },
	} = useHospitalData();
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();

	const createCredentialMutation = useMutation({
		mutationFn: async ({
			request,
			locationIdForInvalidation,
			nurseIdForInvalidation,
		}: {
			request: CreateLocationCredentialRequest;
			nurseIdForInvalidation?: number;
			locationIdForInvalidation?: string;
		}) => {
			track("create_nurse_credential_attempt", { departmentId, request: JSON.stringify(request), locationIdForInvalidation, nurseIdForInvalidation });
			const response = await backendService.createNurseCredential(request, departmentId);
			return response.data;
		},
		onSuccess: async (request, variables) => {
			track("create_nurse_credential_success", { departmentId, request: JSON.stringify(request) });
			if (variables.nurseIdForInvalidation) {
				// Find which locations this nurse had credentials for and refetch those locations
				const nurseCredentials = queryClient.getQueryData<LocationCredentialResponse[]>(getQueryKey(departmentId, variables.nurseIdForInvalidation));
				if (nurseCredentials) {
					const locationIds = Array.from(new Set(nurseCredentials.map((cred) => cred.department_location_id)));
					for (const locationId of locationIds) {
						await queryClient.invalidateQueries({
							queryKey: getLocationQueryKey(departmentId, locationId),
						});
					}
				}
				// Invalidate the nurse query
				await queryClient.invalidateQueries({
					queryKey: getQueryKey(departmentId, variables.nurseIdForInvalidation),
				});
			}
			if (variables.locationIdForInvalidation) {
				// Find all nurses related to this location and refetch them
				const locationCredentials = queryClient.getQueryData<LocationCredentialResponse[]>(
					getLocationQueryKey(departmentId, variables.locationIdForInvalidation),
				);
				if (locationCredentials) {
					const nurseIds = Array.from(new Set(locationCredentials.map((cred) => cred.nurse_id)));
					for (const nurseId of nurseIds) {
						await queryClient.invalidateQueries({
							queryKey: getQueryKey(departmentId, nurseId),
						});
					}
				}
				// Invalidate the location query
				await queryClient.invalidateQueries({
					queryKey: getLocationQueryKey(departmentId, variables.locationIdForInvalidation),
				});
			}
			showSnackbar(t("credential.created.success", { defaultValue: "Credential created successfully" }), "success");
		},
		onError: (error: AxiosError<{ detail: string }>) => {
			track("create_nurse_credential_failed", { departmentId, error: JSON.stringify(error) });
			const errorMessage = error?.response?.data?.detail || t("credential.create.failed", { defaultValue: "Failed to create credential" });
			showSnackbar(errorMessage, "error");
		},
	});

	const deleteCredentialMutation = useMutation({
		mutationFn: async ({
			credentialId,
			locationIdForInvalidation,
			nurseIdForInvalidation,
		}: {
			credentialId: string;
			nurseIdForInvalidation?: number;
			locationIdForInvalidation?: string;
		}) => {
			track("delete_nurse_credential_attempt", { departmentId, credentialId, locationIdForInvalidation, nurseIdForInvalidation });
			await backendService.deleteNurseCredential(credentialId, departmentId);
		},
		onSuccess: async (_, variables) => {
			track("delete_nurse_credential_success", { departmentId, credentialId: variables.credentialId });
			if (variables.nurseIdForInvalidation) {
				// Find which locations this nurse had credentials for and refetch those locations
				const nurseCredentials = queryClient.getQueryData<LocationCredentialResponse[]>(getQueryKey(departmentId, variables.nurseIdForInvalidation));
				if (nurseCredentials) {
					const locationIds = Array.from(new Set(nurseCredentials.map((cred) => cred.department_location_id)));
					for (const locationId of locationIds) {
						await queryClient.invalidateQueries({
							queryKey: getLocationQueryKey(departmentId, locationId),
						});
					}
				}
				// Invalidate the nurse query
				await queryClient.invalidateQueries({
					queryKey: getQueryKey(departmentId, variables.nurseIdForInvalidation),
				});
			}
			if (variables.locationIdForInvalidation) {
				// Find all nurses related to this location and refetch them
				const locationCredentials = queryClient.getQueryData<LocationCredentialResponse[]>(
					getLocationQueryKey(departmentId, variables.locationIdForInvalidation),
				);
				if (locationCredentials) {
					const nurseIds = Array.from(new Set(locationCredentials.map((cred) => cred.nurse_id)));
					for (const nurseId of nurseIds) {
						await queryClient.invalidateQueries({
							queryKey: getQueryKey(departmentId, nurseId),
						});
					}
				}
				// Invalidate the location query
				await queryClient.invalidateQueries({
					queryKey: getLocationQueryKey(departmentId, variables.locationIdForInvalidation),
				});
			}
			showSnackbar(t("credential.deleted.success", { defaultValue: "Credential deleted successfully" }), "success");
		},
		onError: (error: AxiosError<{ detail: string }>) => {
			track("delete_nurse_credential_failed", { departmentId, error: JSON.stringify(error) });
			const errorMessage = error?.response?.data?.detail || t("credential.delete.failed", { defaultValue: "Failed to delete credential" });
			showSnackbar(errorMessage, "error");
		},
	});

	return {
		createCredential: createCredentialMutation.mutateAsync,
		isCreatingCredential: createCredentialMutation.isPending,
		deleteCredential: deleteCredentialMutation.mutateAsync,
		isDeletingCredential: deleteCredentialMutation.isPending,
	};
};
